<template>
    <div class="flex flex-col flex-grow px-6 py-6 bg-gray-50">
        <app-header>
            <h2 class="flex justify-start flex-1 text-2xl font-bold text-gray-800">Organization Settings</h2>
        </app-header>
        <div class="flex pt-24"></div>
        <content v-if="!$store.getters['orgs/isLoading'] && !$store.getters['orgs/detailsLoading']" class="flex flex-col">
            <div class="flex w-full px-6 py-8 mb-6 bg-white rounded-md shadow">
                <div class="w-2/3">
                    <div class="py-1 text-2xl font-semibold">{{ org.name }}</div>
                    <div class="py-1 font-medium text-gray-600">Created on {{ dates().joined }} ({{ dates().ago }})</div>
                    <div class="py-1 font-medium text-gray-600">
                        Subscribed to the
                        <a href="#plan-details" class="font-bold text-primary-500"
                            >{{ org.plan.name }}<span class="text-sm font-semibold text-gray-600">({{ org.plan.schedule }})</span></a
                        >
                        plan
                    </div>
                </div>
                <div class="flex flex-col justify-end w-1/3">
                    <org-avatar
                        v-if="$store.getters['orgs/isAdmin']"
                        @click.native="open = true"
                        :org="org"
                        class="inline-flex items-center justify-center w-32 h-32 text-sm text-4xl bg-gray-400 border-4 border-transparent rounded-full cursor-pointer hover:border-gray-600 "
                    />
                    <org-avatar v-else :org="org" class="w-32 h-32 text-4xl border-4 border-transparent" />
                    <main-modal :open="open" @close="open = false">
                        <div class="px-4 pt-5 pb-4 overflow-hidden transition-all transform bg-white rounded-md shadow-xl sm:max-w-lg sm:w-full sm:p-6">
                            <div>
                                <div>
                                    <h3 class="text-lg font-semibold leading-6 text-gray-900">Profile photo</h3>
                                </div>
                                <div class="flex flex-col justify-center">
                                    <div class="overflow-hidden rounded-md ">
                                        <vue-avatar :width="200" :height="200" :borderRadius="1000" :scale="parseFloat(scale)" ref="vueavatar"></vue-avatar>
                                    </div>
                                    <input class="flex mx-auto mt-2" type="range" min="1" max="3" step="0.02" v-model="scale" />
                                    <span class="flex w-full sm:ml-3 sm:w-auto">
                                        <button
                                            @click="remove"
                                            type="button"
                                            class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-red-700 transition duration-150 ease-in-out hover:underline focus:outline-none sm:text-sm sm:leading-5"
                                        >
                                            Remove photo
                                        </button>
                                    </span>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                                    <button
                                        @click="upload"
                                        type="button"
                                        class="w-full text-base font-medium leading-6 text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow-sm bg-primary-500 btn hover:bg-primary-400 focus:outline-none focus:outline-shadow sm:text-sm sm:leading-5"
                                    >
                                        Save Changes
                                    </button>
                                </span>
                                <span class="flex w-full mt-3 rounded-md shadow-sm sm:mt-0 sm:w-auto">
                                    <button
                                        @click="open = false"
                                        type="button"
                                        class="w-full text-base font-medium leading-6 text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm btn hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline sm:text-sm sm:leading-5"
                                    >
                                        Cancel
                                    </button>
                                </span>
                            </div>
                        </div>
                    </main-modal>
                </div>
            </div>
            <div class="bg-white rounded-md shadow sm:px-0">
                <div class="px-4 py-3 border-b border-gray-200 sm:px-6">
                    <div class="flex flex-wrap items-center justify-between -ml-4 sm:flex-no-wrap">
                        <div class="ml-4">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Basic info</h3>
                        </div>
                    </div>
                </div>
                <form v-if="$store.getters['orgs/isAdmin']" @submit.prevent="save" class="w-full px-6 py-3">
                    <label class="mb-1 font-semibold text-gray-700" for="name">Name</label>
                    <div class="flex flex-col md:flex-row">
                        <input class="input focus:outline-none focus:border-gray-700" id="org-name" type="text" :value="org.name" />
                        <button class="mt-2 text-white btn bg-primary-500 hover:bg-primary-400 md:mt-0 md:ml-2 md:w-20 focus:outline-none">
                            Save
                        </button>
                    </div>
                </form>
                <div v-else class="px-6 py-3">
                    <label class="mb-1 font-semibold text-gray-700" for="name">Name</label>
                    <div class="flex flex-col text-lg font-medium md:flex-row">
                        {{ org.name }}
                    </div>
                </div>
            </div>
            <div class="mt-6 bg-white rounded-md shadow sm:px-0">
                <div class="px-4 py-3 border-b border-gray-200 sm:px-6">
                    <div class="flex flex-wrap items-center justify-between -ml-4 sm:flex-no-wrap">
                        <div class="ml-4">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Members</h3>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col">
                    <div class="py-2 -my-2">
                        <div class="inline-block min-w-full align-middle rounded-b-md">
                            <table class="min-w-full">
                                <thead>
                                    <tr class="border-l border-r border-gray-100">
                                        <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50" />
                                        <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                                            Name
                                        </th>
                                        <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                                            Account type
                                        </th>
                                        <th class="px-6 py-3 border-b border-gray-200 bg-gray-50"></th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white">
                                    <org-member v-for="member in org.memberships" :key="member.user_id" :member="member" />
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <invites-table :org="org" class="mt-6" />
            <org-plan id="plan-details" :plan="org.plan" :org="org" :subscription="sub_details" class="mt-6" />
            <div v-if="$store.getters['orgs/isOwner']" class="mt-6 bg-white rounded-md shadow sm:px-0">
                <div class="px-4 py-3 border-b border-gray-200 sm:px-6">
                    <div class="flex flex-wrap items-center justify-between -ml-4 sm:flex-no-wrap">
                        <div class="ml-4">
                            <h3 class="flex text-lg font-medium leading-6 text-red-500">
                                <svg class="w-5 h-5 mr-2 fill-current" viewBox="0 0 24 24">
                                    <path
                                        d="M8.46777,8.39453l-.00225.00183-.00214.00208ZM18.42188,8.208a1.237,1.237,0,0,0-.23-.17481.99959.99959,0,0,0-1.39941.41114,5.78155,5.78155,0,0,1-1.398,1.77734,8.6636,8.6636,0,0,0,.1333-1.50977,8.71407,8.71407,0,0,0-4.40039-7.582,1.00009,1.00009,0,0,0-1.49121.80567A7.017,7.017,0,0,1,7.165,6.87793l-.23047.1875a8.51269,8.51269,0,0,0-1.9873,1.8623A8.98348,8.98348,0,0,0,8.60254,22.83594.99942.99942,0,0,0,9.98,21.91016a1.04987,1.04987,0,0,0-.0498-.3125,6.977,6.977,0,0,1-.18995-2.58106,9.004,9.004,0,0,0,4.3125,4.0166.997.997,0,0,0,.71534.03809A8.99474,8.99474,0,0,0,18.42188,8.208ZM14.51709,21.03906a6.964,6.964,0,0,1-3.57666-4.40234,8.90781,8.90781,0,0,1-.17969-.96387,1.00025,1.00025,0,0,0-.79931-.84473A.982.982,0,0,0,9.77,14.80957a.99955.99955,0,0,0-.8667.501,8.9586,8.9586,0,0,0-1.20557,4.71777,6.98547,6.98547,0,0,1-1.17529-9.86816,6.55463,6.55463,0,0,1,1.562-1.458.74507.74507,0,0,0,.07422-.05469s.29669-.24548.30683-.2511a8.96766,8.96766,0,0,0,2.89874-4.63269,6.73625,6.73625,0,0,1,1.38623,8.08789,1.00024,1.00024,0,0,0,1.18359,1.418,7.85568,7.85568,0,0,0,3.86231-2.6875,7.00072,7.00072,0,0,1-3.2793,10.457Z"
                                    />
                                </svg>
                                Danger Zone
                            </h3>
                        </div>
                    </div>
                </div>
                <div class="px-4 py-5 sm:p-6">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        Delete this organization
                    </h3>
                    <div class="mt-2 text-sm leading-5 text-gray-500 max-w-xxl">
                        <p>
                            Once you delete this organization, you will lose all data associated with it, and your subscription will be canceled.
                        </p>
                    </div>
                    <div class="mt-5">
                        <button
                            @click="deleteOrgConfirm = true"
                            type="button"
                            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 transition duration-150 ease-in-out bg-red-100 border border-transparent rounded-md hover:bg-red-50 focus:outline-none focus:border-red-300 focus:shadow-outline-red active:bg-red-200 sm:text-sm sm:leading-5"
                        >
                            Delete organization
                        </button>
                    </div>
                </div>
                <div class="px-4 py-5 sm:p-6">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">
                        Cancel your subscription
                    </h3>
                    <div class="mt-2 text-sm leading-5 text-gray-500 max-w-xxl">
                        <p>Your subscription will be immediately canceled and you will retain access until the end of the billing period.</p>
                    </div>
                    <div class="mt-5">
                        <button
                            @click="cancelSubConfirm = true"
                            type="button"
                            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 transition duration-150 ease-in-out bg-red-100 border border-transparent rounded-md hover:bg-red-50 focus:outline-none focus:border-red-300 focus:shadow-outline-red active:bg-red-200 sm:text-sm sm:leading-5"
                        >
                            Cancel subscription
                        </button>
                    </div>
                </div>
                <main-modal :open="cancelSubConfirm" @close="cancelSubConfirm = false">
                    <div class="fixed inset-x-0 bottom-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center">
                        <div
                            class="relative px-4 pt-5 pb-4 overflow-hidden transition-all transform bg-white rounded-lg shadow-xl sm:max-w-lg sm:w-full sm:p-6"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="modal-headline"
                        >
                            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                <button
                                    @click="cancelSubConfirm = false"
                                    type="button"
                                    class="text-gray-400 transition duration-150 ease-in-out hover:text-gray-500 focus:outline-none focus:text-gray-500"
                                    aria-label="Close"
                                >
                                    <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                                    <svg class="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                        />
                                    </svg>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900" id="modal-headline">Cancel subscription</h3>
                                    <div class="mt-2">
                                        <p class="text-sm leading-5 text-gray-500">
                                            Reminder: you will have access until your billing period ends. You can restart your subscription at anytime!
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                                    <button
                                        @click="cancelSubscription"
                                        type="button"
                                        class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white transition duration-150 ease-in-out bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red sm:text-sm sm:leading-5"
                                    >
                                        Cancel subscription
                                    </button>
                                </span>
                                <span class="flex w-full mt-3 rounded-md shadow-sm sm:mt-0 sm:w-auto">
                                    <button
                                        @click="cancelSubConfirm = false"
                                        type="button"
                                        class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5"
                                    >
                                        Dismiss
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                </main-modal>
            </div>
        </content>
    </div>
</template>

<script>
import api from '@/client';
import AppHeader from '@/views/AppHeader';
import OrgPlan from '@/components/OrgPlan';
import OrgMember from '@/components/OrgMember';
import MainModal from '@/components/MainModal';
import OrgAvatar from '@/components/OrgAvatar';
import InvitesTable from '@/components/InvitesTable';
import { VueAvatar } from 'vue-avatar-editor-improved';
import { mapState } from 'vuex';

export default {
    name: 'org-settings',
    components: { AppHeader, OrgMember, MainModal, InvitesTable, OrgAvatar, VueAvatar, OrgPlan },
    metaInfo() {
        return {
            title: this.org.name + ' settings',
        };
    },
    data() {
        return {
            scale: 1,
            open: false,
            cancelSubConfirm: false,
            deleteOrgConfirm: false,
        };
    },
    computed: { ...mapState('orgs', ['org', 'sub_details']) },
    methods: {
        save() {},
        edit() {},
        dates() {
            return {
                joined: this.$moment
                    .utc(this.org.inserted_at)
                    .local()
                    .format('MMM Do YYYY'),
                ago: this.$moment
                    .utc(this.org.inserted_at)
                    .local()
                    .fromNow(),
            };
        },
        remove() {
            api.delete(`org/${this.org.id}/avatar/${this.user.id}`).then(() => {
                this.$store.dispatch('orgs/all');
            });
            this.open = false;
        },
        upload() {
            const img = this.$refs.vueavatar.getImageScaled().toDataURL('image/jpeg');
            api.post('avatars', {
                org_id: this.org.id,
                avatar: {
                    filename: 'avatar.jpeg',
                    binary: img.split(',')[1],
                },
            }).then(() => {
                this.$store.dispatch('orgs/all');
            });
            this.open = false;
        },
        cancelSubscription() {
            this.$store.dispatch('users/cancel_subscription', this.org.id).then(() => {
                this.cancelSubConfirm = false;
            });
        },
    },
};
</script>

<style>
table tr:last-child td {
    border: none !important;
}
table tr:last-child td:first-child {
    border-radius: 0 0 0 0.375rem !important;
    -moz-border-radius: 0 0 0 0.375rem !important;
}
table tr:last-child td:last-child {
    border-radius: 0 0 0.375rem 0 !important;
    -moz-border-radius: 0 0 0.375rem 0 !important;
}
</style>
